import { Injectable, Inject, Provider, APP_INITIALIZER } from '@angular/core';
import { Router } from '@angular/router';
import { ACLService } from '@delon/acl';
import { DA_SERVICE_TOKEN, ITokenModel, ITokenService } from '@delon/auth';
import {
  MenuService,
  SettingsService,
  TitleService,
  _HttpClient
} from '@delon/theme';
import { environment } from '@env/environment';
import { NzIconService } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AppService } from 'src/app/shared/services/app.service';
import { DateFormatService } from 'src/app/shared/services/date-format.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { QuinnyCampaignService } from 'src/app/shared/services/quinny-campaign.service';
import { UserDefaultService } from 'src/app/shared/services/user-default.service';

import { ICONS } from '../../../style-icons';
import { ICONS_AUTO } from '../../../style-icons-auto';
import { isAnonymousPage } from '../net/custom-jwt.interceptor';
import { FcwidgetService } from '../util/fcwidget.service';

import { Buffer } from 'buffer';

/**
 * Used for application startup
 * Generally used to get the basic data of the application, like: Menu Data, User Data, etc.
 */
export function provideStartup(): Provider[] {
  return [
    StartupService,
    {
      provide: APP_INITIALIZER,
      useFactory: (startupService: StartupService) => () =>
        startupService.load(),
      deps: [StartupService],
      multi: true
    }
  ];
}

@Injectable()
export class StartupService {
  constructor(
    iconSrv: NzIconService,
    private menuService: MenuService,
    private settings: SettingsService,
    private aclService: ACLService,
    private titleService: TitleService,
    @Inject(DA_SERVICE_TOKEN) private tokenService: ITokenService,
    private http: _HttpClient,
    private router: Router,
    private msg: NzMessageService,
    private fc: FcwidgetService,
    private appSrv: AppService,
    private notifySrv: NotificationService,
    private dateFormatService: DateFormatService,
    private udService: UserDefaultService,
    private quinnyCampaignSrv: QuinnyCampaignService
  ) {
    iconSrv.addIcon(...ICONS_AUTO, ...ICONS);
    iconSrv.fetchFromIconfont({
      scriptUrl:
        'https://cdn.qne.cloud/qca/assets/js/font_4192101_s95i3q8tieg.js'
    });
  }

  load(): Promise<any> {
    return new Promise((resolve, _) => {
      this.viaHttp(resolve);
    });
  }

  private viaHttp(resolve: any) {
    const app = {
      name: 'QNE AI Cloud Accounting',
      description: 'QNE AI Cloud Accounting',
      bannerLogo: './assets/img/logo-banner.png',
      squareLogo: './assets/img/logo-square.png'
    };
    // Application information: including site name, description, year
    this.settings.setApp(app);
    // Set page title suffix
    this.titleService.suffix = app.name;

    const redirectUrl = this.handleAutoLoginRedirect();

    if (isAnonymousPage(window.location.href) || !this.authorized()) {
      resolve(null);
      return;
    }
    const isReportViewer =
      window.location.href.indexOf('reporting/viewer/') !== -1;
    const isViewerPage = window.location.href.indexOf('#/full/viewer/') !== -1;
    if (isReportViewer) {
      // If this is opening report viewer or designer page, skip the setup process of angular app
      const user: any = this.settings.getData('user');
      const acl = user.acl;
      if (acl) {
        this.aclService.setFull(acl.full);
        this.aclService.setAbility(acl.abilities);
      }
      resolve(null);
    } else if (isViewerPage) {
      resolve(null);
    } else {
      this.dateFormatService.getDateFormat().subscribe({});
      const terminalId = this.appSrv.getTerminalId();
      const terminalName = this.appSrv.getTerminalName();
      this.http.get(`api/app/qca`, { terminalId, terminalName }).subscribe({
        next: (res: any) => {
          // Renew token
          if (res.token) this.tokenService.set(res.token);

          this.appSrv.startListenSwitchAccount();

          const acl = res.acl;

          // User information: including name, avatar, email address
          const currentUser = this.settings.user || {};
          const dateFormat = currentUser['dateFormat'] ?? res.user?.dateFormat;
          this.settings.setUser({ ...res.user, acl: acl, dateFormat: dateFormat });
          this.aclService.setFull(acl.full);
          this.aclService.setAbility(acl.abilities);

          this.dateFormatService.loadTenantDateFormat();

          // Load default settings after user data is set
          this.udService.loadDefaultSettingsToCache(res.user.userId);

          // Set app menu
          const menu = res.menu ?? [];
          this.menuService.add(menu);

          const country = res.user?.company?.country ?? res.user?.country;
          this.fc.setCountry(country);
          // Widget loads only on first open (no preload) so Lighthouse never sees Freshchat / third-party cookie.

          const company = res.user.company;
          if (company) {
            if (company.bannerLogo) app.bannerLogo = company.bannerLogo;
            if (company.squareLogo) app.squareLogo = company.squareLogo;

            this.settings.setApp(app);

            this.notifySrv.init(company.tenantId, res.user.userId);

            // Show Quinny AI campaign modal for eligible users (only on login/startup)
            setTimeout(() => this.quinnyCampaignSrv.showCampaignModal(), 2000);

            if (!company.isSample && !company.initialized) {
              this.goto('full/setup/welcome');
            } else if (redirectUrl) {
              setTimeout(() => this.router.navigateByUrl(redirectUrl));
            }
          } else {
            setTimeout(() =>
              this.router.navigate([
                './passport/complete-signup/choose-company'
              ])
            );
          }
          resolve(null);
        },
        error: err => {
          console.error(err);
          this.handleStartupError();
          resolve(null);
        }
      });
    }
  }

  handleAutoLoginRedirect(): string {
    let url = window.location.href.replace(window.location.origin, '');
    const queryStartAt = url.indexOf('?');
    const queryString = url.slice(queryStartAt + 1);
    const searchParams = new URLSearchParams(queryString);
    const encodedToken = searchParams.get('_token');
    if (encodedToken) {
      try {
        const tokenJson = Buffer.from(
          decodeURIComponent(encodedToken),
          'base64'
        ).toString();
        const token = JSON.parse(tokenJson);
        this.tokenService.set(token);

        searchParams.delete('_token');
        if (url.startsWith('/#')) url = url.slice(2, queryStartAt);
        else url = `${url.slice(0, queryStartAt)}`;
        const queryString = searchParams.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
        return url;
      } catch (ex) {
        console.error(ex);
        return '';
      }
    }
    return '';
  }

  private gotoLogin() {
    setTimeout(() => this.router.navigateByUrl(this.tokenService.login_url!));
  }
  private goto(url: string) {
    setTimeout(() => this.router.navigate([url]));
  }

  private handleStartupError() {
    this.msg.error(
      'Failed to start application, kindly contact our technical support for assistance!'
    );

    // If in production and not anonymous page, redirect to login page
    if (environment.production && !isAnonymousPage(window.location.href)) {
      this.gotoLogin();
    }
  }
  private authorized(): boolean {
    const item = this.tokenService.get() as ITokenModel;
    const expired = item.expired || 0;
    const curTime = new Date().valueOf();
    return expired > curTime;
  }
}
